import { JSX, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/react'

import { LeidButton, LeidButtonWhiteOutlined } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

import { OpenDoorPINStepData } from '../models'
import { PinCodeForDeviceResponse } from '@leid/api-assets'
import { FaLockOpen } from 'react-icons/fa'
import { getPINCodeDates, toPrettifiedPin } from '../../../utils/devices'
import { pretifyDate } from 'utils/dates'

type Props = {
  data: OpenDoorPINStepData
  onStepSuccess: () => void
}

const OpenDoorsPinStep = (props: Props): JSX.Element => {
  const { data, onStepSuccess } = props

  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [triggerInProgress, setTriggerInProgress] = useState(false)
  const [fetchedCode, setFetchedCode] = useState<PinCodeForDeviceResponse | null>(null)

  const getDoorPIN = async (): Promise<boolean> => {
    const { startDate, endDate } = getPINCodeDates()

    const response = await apiClient.getPinCodeForDevice({
      deviceId: data.deviceId,
      locationId: data.locationId,
      startDate,
      endDate,
      variance: 1,
    })

    if (!response.success || !response.data) {
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      console.error('Failed to get PIN')
      return false
    }

    setFetchedCode(response.data)
    return true
  }

  const handleAlreadyInsideButton = () => {
    onStepSuccess()
  }

  const handleGetPINCode = async () => {
    setTriggerInProgress(true)
    await getDoorPIN()
    setTriggerInProgress(false)
  }

  return (
    <>
      {fetchedCode && (
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'} flexWrap={'wrap'} width="100%">
          <Flex flexDirection={'row'} alignItems={'center'} gap={3}>
            <FaLockOpen size={24} />
            <Text fontSize="xl" fontWeight="bold" textAlign="center">
              {toPrettifiedPin(fetchedCode?.pinCode)}
            </Text>
          </Flex>
          <Text fontSize="xs" opacity={0.5}>{`${pretifyDate(fetchedCode.startDate)} | ${pretifyDate(
            fetchedCode.stopDate,
          )}`}</Text>
        </Box>
      )}

      <LeidButton onClick={handleGetPINCode}>
        <Flex flexDirection="row" justifyContent="safe center" alignItems="center" width="100%" gridGap="12px">
          {triggerInProgress && <Loader size="md" width="auto" margin="0" />}
          {t(`getLockPinForDoor`, { name: data.doorName })}
        </Flex>
      </LeidButton>
      <LeidButtonWhiteOutlined onClick={handleAlreadyInsideButton}>{t('alreadyInside')}</LeidButtonWhiteOutlined>
    </>
  )
}

export default OpenDoorsPinStep
